import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { addDays, differenceInMonths } from "date-fns";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createEmployee,
  getEmployee,
  removeEmployeeFromListById,
  resetSuccess,
  resetError,
  selectProcesses,
} from "../process/processSlices";
import Grid from "@mui/material/Grid";
import { useNavigate, NavLink as RouterLink } from "react-router-dom";

import { selectPreference, toggleThis } from "../preferences/preferencesSlice";
import {
  Stack,
  Button,
  Typography,
  Skeleton,
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  useTheme,
  FormControlLabel,
  Switch,
  Tooltip,
  Badge,
  Fab,
  Avatar,
  Tabs,
  Tab,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
} from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import {
  Assignment,
  AssignmentTurnedIn,
  ChevronRight,
  FilterList,
  Flight,
  FlightTakeoff,
  HowToReg,
  Block,
  PeopleOutline,
  Refresh,
  RemoveRedEye,
  VideoLibrary,
  Share,
  ThumbDown,
  ThumbUp,
  ViewList,
  ViewModule,
} from "@mui/icons-material";
import { apolloClient } from "../../apollo/graphql";
import { GET_EMPLOYEE_DASHBOARD_STATS } from "../../apollo/queries/processes";
import Image from "mui-image";

import Paging from "../../components/Layout/Paging";
import {
  CompletionStatus,
  Employee,
  EmployeeStatus,
  Nationalities,
  NotificationMessageTypes,
} from "./types/processTypes";
import {
  StyledTableRow,
  StyledTableCell,
  StyledTableCellLabel,
  StyledTableCellValue,
} from "../../styles/tableStyles";
import CustomDialog from "../../components/modals/CustomDialog";
import ShareCvs from "./components/ShareCvs";
import { MedicalStatusChip } from "./components/MedicalStatusChip";
import { useTranslation } from "react-i18next";
import { Company } from "../setups/types/setupTypes";
import { getCompany, selectSetups } from "../setups/setupSlices";
import { VisaEntry } from "./VisaEntry";
import { useEmployeeListFetch } from "./hooks/useEmployeeListFetch";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import { ClientTypes, Role } from "../auth/types/authType";
import { selectAuth } from "../auth/authSlice";
import Toast from "../../components/Layout/Toast";
import { CompanyFilter } from "../../components/filter/CompanyFilter";
import Search from "../../components/search/Search";
import FilterSidebar from "../../components/filter/FilterSidebar";
import { tt } from "../../data/langUtility";

interface Props {
  isPremium: boolean;
}

type CandidateTab =
  | "available"
  | "selected"
  | "contracted"
  | "processing"
  | "flightBooked"
  | "departed"
  | "other";

const TAB_ICONS: Record<CandidateTab, typeof PeopleOutline> = {
  available: PeopleOutline,
  selected: HowToReg,
  contracted: AssignmentTurnedIn,
  processing: Assignment,
  flightBooked: Flight,
  departed: FlightTakeoff,
  other: Block,
};

const PROCESSING_STATUSES: EmployeeStatus[] = [
  EmployeeStatus.visa_issued,
  EmployeeStatus.labour_issued,
];

const FLIGHT_BOOKED_STATUSES: EmployeeStatus[] = [EmployeeStatus.flight_booked];

const DEPARTED_STATUSES: EmployeeStatus[] = [EmployeeStatus.departed];

const OTHER_STATUSES: EmployeeStatus[] = [
  EmployeeStatus.discontinued,
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.has_complain,
  EmployeeStatus.contract_completed,
];

const POST_SELECTION_TABS: CandidateTab[] = [
  "contracted",
  "processing",
  "flightBooked",
  "departed",
  "other",
];

function isPostSelectionTab(tab: CandidateTab): boolean {
  return POST_SELECTION_TABS.includes(tab);
}

const CANDIDATE_TABS: {
  value: CandidateTab;
  labelKey: string;
  statuses: EmployeeStatus[];
}[] = [
  {
    value: "available",
    labelKey: "processTabs.available",
    statuses: [EmployeeStatus.new],
  },
  {
    value: "selected",
    labelKey: "processTabs.selected",
    statuses: [EmployeeStatus.selected],
  },
  {
    value: "contracted",
    labelKey: "processTabs.contracted",
    statuses: [EmployeeStatus.contracted],
  },
  {
    value: "processing",
    labelKey: "processTabs.processing",
    statuses: PROCESSING_STATUSES,
  },
  {
    value: "flightBooked",
    labelKey: "processTabs.flightBooked",
    statuses: FLIGHT_BOOKED_STATUSES,
  },
  {
    value: "departed",
    labelKey: "processTabs.departed",
    statuses: DEPARTED_STATUSES,
  },
  {
    value: "other",
    labelKey: "processTabs.other",
    statuses: OTHER_STATUSES,
  },
];

function getStatusFilterForTab(activeTab: CandidateTab) {
  const tabConfig = CANDIDATE_TABS.find((tab) => tab.value === activeTab);
  if (!tabConfig) {
    return { status: EmployeeStatus.new };
  }
  if (tabConfig.statuses.length === 1) {
    return { status: tabConfig.statuses[0] };
  }
  return { statuses: tabConfig.statuses };
}

/** Whether `emp` would be returned by the list query for the given tab. */
function employeeMatchesActiveTab(
  emp: Employee,
  activeTab: CandidateTab,
): boolean {
  const tab = CANDIDATE_TABS.find((t) => t.value === activeTab);
  if (!tab) return true;
  const s = emp.status as EmployeeStatus | undefined;
  return tab.statuses.some((st) => st === s);
}

function sumDashboardCountsForStatuses(
  countsByStatus: Record<string, number>,
  statuses: EmployeeStatus[],
): number {
  return statuses.reduce((sum, s) => sum + (countsByStatus[s] ?? 0), 0);
}

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function getStatusChipColor(
  status: string | undefined,
): "default" | "success" | "error" | "warning" | "info" {
  if (!status) return "default";
  if (status === EmployeeStatus.pending) return "warning";
  if (
    status === EmployeeStatus.departed ||
    status === EmployeeStatus.contract_completed
  )
    return "success";
  if (
    status === EmployeeStatus.discontinued ||
    status === EmployeeStatus.stopped ||
    status === EmployeeStatus.lost ||
    status === EmployeeStatus.returned ||
    status === EmployeeStatus.has_complain
  )
    return "error";
  if (
    status === EmployeeStatus.flight_booked ||
    status === EmployeeStatus.visa_issued ||
    status === EmployeeStatus.labour_issued
  )
    return "warning";
  if (
    status === EmployeeStatus.selected ||
    status === EmployeeStatus.contracted
  )
    return "info";
  return "default";
}

function renderNationalityFlag(nationality: string | undefined) {
  if (nationality === Nationalities.ethiopian) {
    return (
      <img alt="ethioFlag" height="10px" width="20px" src="/ethiopiaFlag.png" />
    );
  }
  if (nationality === Nationalities.kenyan) {
    return (
      <img alt="kenyaFlag" height="10px" width="20px" src="/kenyaFlag.png" />
    );
  }
  if (nationality === Nationalities.ugandan) {
    return (
      <img alt="ugandaFlag" height="10px" width="20px" src="/ugandaFlag.png" />
    );
  }
  return (
    <img
      alt="philippineFlag"
      height="10px"
      width="20px"
      src="/philippineFlag.png"
    />
  );
}

export const Candidates = ({ isPremium }: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { user } = useAppSelector(selectAuth);
  const { selectedCompany } = useAppSelector(selectSetups);
  const [company, setCompany] = useState<Company>();
  const cvLang =
    selectedCompany?.cvTemplate?.cvLang ??
    company?.cvTemplate?.cvLang ??
    user?.company?.cvTemplate?.cvLang ??
    "ar";

  const {
    employeesWithCount: { employees, totalCount },
    selectedEmployee,
    // notificationsWithCount: { notifications, totalCount: notifyCount },
    loading,
    success,
    error,
  } = useAppSelector(selectProcesses);
  const {
    searchText,
    nationality,
    maritalStatus,
    religion,
    profession,
    arabic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    agencyId,
    agentId,
    cocCompleted: coc,
    medicalStatus: medical,
    biometricsCompleted: biometrics,
  } = useAppSelector(selectPreference);

  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [sharedEmployeeIds, setSharedEmployeeIds] = useState<string[]>([]);
  const [openVisa, setOpenVisa] = useState(false);
  const [selectCandidates, setSelectCandidates] = useState<boolean[]>([false]);
  const [selectAll, setSelectAll] = useState(false);
  const [activeTab, setActiveTab] = useState<CandidateTab>("available");
  const [viewMode, setViewMode] = useState<"card" | "table">("table");
  // Staff can hide pipeline tabs before handing the screen to a customer.
  const [showProcessed, setShowProcessed] = useState(true);
  const [statusDashboardCounts, setStatusDashboardCounts] = useState<
    Record<string, number>
  >({});
  const [dashboardStatsLoading, setDashboardStatsLoading] = useState(false);
  const [dashboardReloadNonce, setDashboardReloadNonce] = useState(0);
  const [videoPreview, setVideoPreview] = useState<{
    url: string;
    name: string;
  } | null>(null);

  const visibleCandidateTabs = useMemo(
    () =>
      CANDIDATE_TABS.filter(
        (tab) => !isPostSelectionTab(tab.value) || showProcessed,
      ),
    [showProcessed],
  );

  const dashboardStatsVariables = useMemo(
    () => ({
      searchText: searchText && searchText.length > 0 ? searchText : undefined,
      nationality,
      maritalStatus,
      religion,
      profession,
      arabic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      cocCompleted:
        coc && coc === CompletionStatus.completed
          ? true
          : coc === CompletionStatus.not_completed
            ? false
            : undefined,
      medicalStatus: medical,
      biometricsCompleted:
        biometrics && biometrics === CompletionStatus.completed
          ? true
          : biometrics === CompletionStatus.not_completed
            ? false
            : undefined,
    }),
    [
      searchText,
      nationality,
      maritalStatus,
      religion,
      profession,
      arabic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      coc,
      medical,
      biometrics,
    ],
  );

  const tabBadgeCountLabel = useCallback(
    (tab: (typeof CANDIDATE_TABS)[number]) => {
      if (dashboardStatsLoading) return "…";
      return String(
        sumDashboardCountsForStatuses(statusDashboardCounts, tab.statuses),
      );
    },
    [dashboardStatsLoading, statusDashboardCounts],
  );
  const navigate = useNavigate();

  // If pipeline tabs are hidden while one is active, fall back to Available.
  useEffect(() => {
    if (!showProcessed && isPostSelectionTab(activeTab)) {
      setActiveTab("available");
    }
  }, [showProcessed, activeTab]);

  const viewCvHandler = (id: string) => {
    //dispatch(setSelectedEmployee(employees.find((e) => e._id === id)));
    if (isPremium) navigate(`/app/cvp/${id}`);
    else navigate(`/app/cvp/${id}`);
  };

  const playEmployeeVideo = (row: Employee) => {
    if (!row.employeeVideo) return;
    const name = `${row.givenName ?? ""} ${row.surName ?? ""}`.trim();
    setVideoPreview({ url: row.employeeVideo, name: name || "Employee video" });
  };
  const dialogClose = () => {
    dispatch(resetSuccess());
    setOpen(false);
  };

  useEffect(() => {
    if (!user?.company) return;

    const usesCompanyFilter =
      user.company.type === ClientTypes.Moderator ||
      user.company.type === ClientTypes.LocalAgency;

    if (!usesCompanyFilter) {
      setCompany(user.company);
    }

    dispatch(getCompany(user.company._id as string));
  }, [user, dispatch]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboardTabCounts() {
      setDashboardStatsLoading(true);
      try {
        const { data } = await apolloClient.query({
          query: GET_EMPLOYEE_DASHBOARD_STATS,
          variables: dashboardStatsVariables,
          fetchPolicy: "network-only",
        });
        if (cancelled) return;

        const stats = data?.employeeDashboardStats;
        const groupByCategory: Record<string, number> = {};
        for (const row of stats?.byStatus ?? []) {
          groupByCategory[row.status] =
            (groupByCategory[row.status] ?? 0) + row.count;
        }
        setStatusDashboardCounts(groupByCategory);
      } finally {
        if (!cancelled) {
          setDashboardStatsLoading(false);
        }
      }
    }

    void loadDashboardTabCounts();
    return () => {
      cancelled = true;
    };
  }, [dashboardStatsVariables, dashboardReloadNonce]);

  useEffect(() => {
    if (company?._id && company._id !== "-1") {
      dispatch(getCompany(company._id));
    }
  }, [company?._id, dispatch]);
  // useEffect(() => {
  //   if (success && selectedShortLink && selectedShortLink.link) {
  //     setOpen(true);
  //     setShortLink(selectedShortLink);
  //   }
  // }, [success, selectedShortLink]);

  // Filter/search changes must reset paging (tab clicks already do), otherwise
  // a smaller result set leaves `skip` past the end and the page renders
  // empty. Adjusting state during render (instead of an effect) avoids
  // dispatching one fetch with the old page first.
  const filtersResetKey = JSON.stringify([
    searchText,
    nationality,
    maritalStatus,
    religion,
    profession,
    arabic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    agencyId,
    agentId,
    coc,
    medical,
    biometrics,
  ]);
  const [prevFiltersResetKey, setPrevFiltersResetKey] =
    useState(filtersResetKey);
  if (filtersResetKey !== prevFiltersResetKey) {
    setPrevFiltersResetKey(filtersResetKey);
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  }

  const listFetchParams = useMemo(() => {
    const skipRows = currentPage * rowsPerPage;
    const statusFilter = getStatusFilterForTab(activeTab);

    return {
      skip: skipRows,
      take: rowsPerPage,
      ...statusFilter,
      searchText: searchText && searchText.length > 0 ? searchText : undefined,
      nationality,
      maritalStatus,
      religion,
      profession,
      arabic,
      english,
      ageStart,
      ageEnd,
      experienceYears,
      agencyId,
      agentId,
      cocCompleted:
        coc && coc === CompletionStatus.completed
          ? true
          : coc === CompletionStatus.not_completed
            ? false
            : undefined,
      medicalStatus: medical,
      biometricsCompleted:
        biometrics && biometrics === CompletionStatus.completed
          ? true
          : biometrics === CompletionStatus.not_completed
            ? false
            : undefined,
    };
  }, [
    searchText,
    nationality,
    maritalStatus,
    religion,
    profession,
    arabic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    currentPage,
    rowsPerPage,
    agencyId,
    agentId,
    coc,
    medical,
    biometrics,
    activeTab,
  ]);

  const { refreshList } = useEmployeeListFetch(listFetchParams);

  const refreshCandidateData = useCallback(() => {
    refreshList();
    setDashboardReloadNonce((n) => n + 1);
  }, [refreshList]);

  const handleRefresh = useCallback(() => {
    refreshCandidateData();
  }, [refreshCandidateData]);

  const dialogCloseVisa = useCallback(() => {
    dispatch(resetSuccess());
    dispatch(resetError());
    setOpenVisa(false);
    refreshCandidateData();

    if (!selectedEmployee?._id) return;
    if (!employeeMatchesActiveTab(selectedEmployee, activeTab)) {
      dispatch(removeEmployeeFromListById(String(selectedEmployee._id)));
    }
  }, [activeTab, dispatch, refreshCandidateData, selectedEmployee]);

  // useEffect(() => {
  //   setEmployees(allEmployees.filter((e) => e.visa && e.visa.visaNumber));
  // }, [allEmployees]);

  // useEffect(() => {

  //   dispatch(fetchShortLinks(""));
  // }, [dispatch]);

  useEffect(() => {
    setTotal(totalCount as number);
    const checkBoxes = [...Array(totalCount)].map((e, i) => {
      return false;
    });
    setSelectCandidates(checkBoxes);
    //console.log(checkBoxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const shareList = () => {
    const employeeIds: string[] = [];
    for (let i = 0; i < totalCount; i++) {
      if (selectCandidates[i] === true)
        employeeIds.push(employees[i]._id as string);
    }
    const emp = employees[0];
    if (emp) {
      setSharedEmployeeIds(employeeIds);
      //dispatch(generateLink({ employeeIds }));
      setOpen(true);
    }
    //console.log(employees[0]);
  };
  const shareSingle = (employeeId: string) => {
    setSharedEmployeeIds([employeeId]);
    // const emp = employees.find((e) => e._id === employeeId);
    // if (emp) {

    //   dispatch(generateLink({ employeeIds: [employeeId] }));
    // }
    setOpen(true);
  };
  const selectCandidate = async (employee: Employee) => {
    dispatch(resetSuccess());
    dispatch(resetError());
    const result = await dispatch(getEmployee(employee._id as string));
    if (!getEmployee.fulfilled.match(result)) return;
    setOpenVisa(true);
  };

  const justSelect = async (employee: Employee) => {
    const result = await dispatch(getEmployee(employee._id as string));
    if (!getEmployee.fulfilled.match(result)) return;
    let values = result.payload as Employee;
    const currentDate = new Date();
    values = {
      ...values,
      status: EmployeeStatus.selected,
      notificationMessage: NotificationMessageTypes.selected as string,
      partner: company,
      selectionDueDate: addDays(currentDate, 21),
    };

    const createResult = await dispatch(createEmployee(values));
    if (createEmployee.fulfilled.match(createResult)) {
      refreshCandidateData();
      if (!employeeMatchesActiveTab(createResult.payload, activeTab)) {
        dispatch(removeEmployeeFromListById(String(createResult.payload._id)));
      }
    }
  };
  const deSelectCandidate = async (employeeId: string) => {
    const result = await dispatch(getEmployee(employeeId));
    if (!getEmployee.fulfilled.match(result)) return;
    let values = result.payload as Employee;
    values = {
      ...values,
      status: EmployeeStatus.new,
      notificationMessage: NotificationMessageTypes.deSelected as string,
      clearVisa: true,
      selectionDueDate: new Date(),
    };
    const createResult = await dispatch(createEmployee(values));
    if (createEmployee.fulfilled.match(createResult)) {
      refreshCandidateData();
      if (!employeeMatchesActiveTab(createResult.payload, activeTab)) {
        dispatch(removeEmployeeFromListById(String(createResult.payload._id)));
      }
    }
  };

  const handleDrawerOpen = () => {
    dispatch(toggleThis({ type: "Filter", newValue: true }));
  };
  // const openShareDialogue = (linkId: string) => {
  //   setOpen(true);
  //   setShortLink(sharedLinks.find((s) => s._id === linkId) as ShortLink);
  // };
  const changeSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAll = event.target.checked;
    setSelectAll(selectedAll);
    const checkBoxes = [...Array(totalCount)].map((e, i) => {
      return selectedAll;
    });
    setSelectCandidates(checkBoxes);
    //console.log(selectCandidates);
  };

  const changeCheckBox = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const tempSelected = [...selectCandidates];
    tempSelected[index] = event.target.checked;
    setSelectCandidates(tempSelected);
  };

  function getDisplayName(company?: Company) {
    if (company) {
      if (theme.direction === "ltr")
        return company.displayName.length > 10
          ? company.displayName.substring(0, company.displayName.indexOf(" "))
          : company.displayName;
      return company.nameAmharic;
    }
    return "";
  }

  const showMedicalColumn =
    activeTab === "available" ||
    activeTab === "selected" ||
    activeTab === "contracted";

  return (
    <>
      <Helmet>
        <title> {`${t("candidates")} | ${t("platform")}`}</title>
      </Helmet>
      <Box
        sx={{
          m: { xs: 0, md: 1 },
          px: { xs: 0, md: 2 },
        }}
      >
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent="space-between"
          >
            {user &&
              user.company &&
              (user.company.type === ClientTypes.Moderator ||
                user.company.type === ClientTypes.LocalAgency) && (
                <CompanyFilter setCompany={setCompany} includeAll={false} />
              )}

            <Search initialValue={searchText} />
            <Tooltip title={t("filter")}>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleDrawerOpen}
                size="large"
                sx={{
                  marginRight: theme.direction === "ltr" ? "0px" : "-12px",
                  marginLeft: theme.direction === "rtl" ? "0px" : "-12px",
                  // ...(isFilterOpen && { display: "none" }),
                }}
              >
                <FilterList />
              </Button>
            </Tooltip>
          </Stack>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Tooltip title={t("refresh")}>
              <span>
                <Button
                  color="secondary"
                  variant="contained"
                  disableElevation
                  size="small"
                  startIcon={
                    <Refresh
                      sx={{
                        animation:
                          loading === "pending"
                            ? `${refreshSpin} 0.8s linear infinite`
                            : "none",
                      }}
                    />
                  }
                  onClick={handleRefresh}
                  disabled={loading === "pending"}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 2,
                    flexShrink: 0,
                  }}
                >
                  {t("refresh")}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.dark, 0.85),
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, value) => {
                  setActiveTab(value as CandidateTab);
                  setCurrentPage(0);
                  setSelectAll(false);
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="secondary"
                indicatorColor="secondary"
                aria-label="candidate status tabs"
                sx={{
                  minHeight: 42,
                  "& .MuiTabs-flexContainer": {
                    justifyContent: { xs: "flex-start", md: "center" },
                  },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: 3,
                    backgroundColor: theme.palette.common.white,
                    transition: theme.transitions.create(["left", "width"], {
                      duration: theme.transitions.duration.standard,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                  },
                  "& .MuiTab-root": {
                    minHeight: 42,
                    borderRadius: 1.5,
                    gap: 0.75,
                    textTransform: "none",
                    fontWeight: 700,
                    color: alpha(theme.palette.common.white, 0.72),
                    transition: theme.transitions.create(
                      ["color", "background-color", "transform"],
                      {
                        duration: theme.transitions.duration.short,
                        easing: theme.transitions.easing.easeOut,
                      },
                    ),
                  },
                  "& .MuiTab-root:hover": {
                    color: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.12),
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.22),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {visibleCandidateTabs.map((tab, index) => {
                  const TabIcon = TAB_ICONS[tab.value];
                  return (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      icon={<TabIcon sx={{ fontSize: "1.125rem" }} />}
                      iconPosition="start"
                      label={
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                        >
                          <span>{`${t(tab.labelKey)} (${tabBadgeCountLabel(tab)})`}</span>
                          {index < visibleCandidateTabs.length - 1 && (
                            <ChevronRight
                              sx={{ fontSize: "1rem", opacity: 0.4 }}
                            />
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>
            {!isPostSelectionTab(activeTab) && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => {
                  if (v !== null) setViewMode(v as "card" | "table");
                }}
                size="small"
                sx={{
                  flexShrink: 0,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
                aria-label="view mode"
              >
                <ToggleButton value="card" aria-label="card view">
                  <Tooltip title="Card view">
                    <ViewModule fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <Tooltip title="Table view">
                    <ViewList fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            {/* <FormControlLabel
              control={
                <Switch
                  checked={showProcessed}
                  onChange={(e) => setShowProcessed(e.target.checked)}
                  color="secondary"
                />
              }
              label={t("showAllTabs")}
              sx={{ flexShrink: 0 }}
            /> */}
          </Stack>
          <>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ width: "100%" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack justifyContent="center" sx={{ mt: 1 }} spacing={1}>
                  {loading === "pending" ? (
                    <Grid
                      container
                      justifyContent="center"
                      sx={{ mt: 1 }}
                      spacing={1}
                    >
                      {[...Array(10)].map((e, i) => (
                        <Grid
                          container
                          item
                          lg={3}
                          md={4}
                          sm={6}
                          xs={12}
                          direction="column"
                          display="flex"
                          alignItems="center"
                          key={i}
                        >
                          <Skeleton
                            variant="rounded"
                            width={180}
                            height={180}
                            style={{ margin: "5px" }}
                          />
                          <Skeleton variant="text" height={25} width={180} />
                          <Skeleton variant="text" height={25} width={180} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : employees.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        alignItems: "center",
                      }}
                    >
                      <Image
                        src="/No_Data.svg"
                        alt="No Data"
                        width={250}
                        height={120}
                        shift="top"
                        distance="2rem"
                        shiftDuration={320}
                        style={{
                          borderRadius: "50%",
                        }}
                      />
                      <Typography variant="h5">
                        {t("no_candidates_found")}
                      </Typography>
                    </Box>
                  ) : isPostSelectionTab(activeTab) ? (
                    <TableContainer
                      component={Paper}
                      sx={{ mt: 1, width: "100%", position: "relative" }}
                    >
                      <Table
                        size="medium"
                        aria-label="pipeline employees"
                        stickyHeader
                      >
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell
                              width="2%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("sno")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="5%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("photo")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="18%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("name")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="12%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {te("nationality")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="10%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("status")}
                            </StyledTableCell>
                            {showMedicalColumn && (
                              <StyledTableCell
                                width="8%"
                                sx={{ textTransform: "uppercase" }}
                              >
                                {te("medical")}
                              </StyledTableCell>
                            )}
                            <StyledTableCell
                              width="10%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("visa_number")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="18%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("employer")}
                            </StyledTableCell>
                            {user?.company?.type === ClientTypes.Moderator ? (
                              <>
                                <StyledTableCell
                                  width="10%"
                                  sx={{ textTransform: "uppercase" }}
                                >
                                  {t("epro")}
                                </StyledTableCell>
                                <StyledTableCell
                                  width="10%"
                                  sx={{ textTransform: "uppercase" }}
                                >
                                  {t("pro")}
                                </StyledTableCell>
                              </>
                            ) : (
                              <StyledTableCell
                                width="15%"
                                sx={{ textTransform: "uppercase" }}
                              >
                                {t("partner")}
                              </StyledTableCell>
                            )}
                            <StyledTableCell
                              width="5%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("actions")}
                            </StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {employees.map((row, index) => (
                            <StyledTableRow key={row._id}>
                              <StyledTableCell>
                                <Typography color="primary" variant="h6">
                                  {(
                                    currentPage * rowsPerPage +
                                    index +
                                    1
                                  ).toLocaleString(
                                    theme.direction === "rtl"
                                      ? "ar-SA"
                                      : "en-US",
                                  )}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Avatar
                                  component={RouterLink}
                                  alt="avatar"
                                  src={row.facePhoto ? row.facePhoto : ""}
                                  sx={{
                                    cursor: "pointer",
                                    height: 40,
                                    width: 40,
                                  }}
                                  to={`/app/cvp/${row._id}`}
                                />
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ color: "primary.main", fontWeight: 700 }}
                              >
                                {row.givenName + " - " + row.passportNumber}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {renderNationalityFlag(
                                    row.additionalData?.nationality as string,
                                  )}
                                  <Typography
                                    sx={{ fontSize: "13px", fontWeight: 700 }}
                                    color="secondary"
                                    textTransform="capitalize"
                                  >
                                    {te(
                                      row.additionalData?.nationality as string,
                                    )}
                                  </Typography>
                                </Stack>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Chip
                                  label={te(row.status as string)}
                                  color={getStatusChipColor(row.status)}
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                />
                              </StyledTableCell>
                              {showMedicalColumn && (
                                <StyledTableCell>
                                  <MedicalStatusChip
                                    status={row.medicalStatus}
                                  />
                                </StyledTableCell>
                              )}
                              <StyledTableCell
                                sx={{
                                  color: "secondary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {row.visa?.visaNumber}
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{
                                  color: "secondary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {row.visa?.passportNumber && row.visa?.fullName
                                  ? `${row.visa.passportNumber} - ${row.visa.fullName}`
                                  : ""}
                              </StyledTableCell>
                              {user?.company?.type === ClientTypes.Moderator ? (
                                <>
                                  <StyledTableCell
                                    sx={{
                                      color: "primary.main",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {getDisplayName(row.owner)}
                                  </StyledTableCell>
                                  <StyledTableCell
                                    sx={{
                                      color: "primary.main",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {getDisplayName(row.partner)}
                                  </StyledTableCell>
                                </>
                              ) : (
                                <StyledTableCell
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 700,
                                  }}
                                >
                                  {user?.company &&
                                  user.company.type === ClientTypes.ForeignAgent
                                    ? getDisplayName(row.owner)
                                    : getDisplayName(row.partner)}
                                </StyledTableCell>
                              )}
                              <StyledTableCell>
                                <Stack
                                  direction="row"
                                  spacing={0}
                                  alignItems="center"
                                  flexWrap="nowrap"
                                >
                                  <Tooltip title="View CV">
                                    <IconButton
                                      color="secondary"
                                      onClick={() =>
                                        viewCvHandler(row._id as string)
                                      }
                                      size="small"
                                    >
                                      <RemoveRedEye />
                                    </IconButton>
                                  </Tooltip>
                                  {row.employeeVideo && (
                                    <Tooltip title="Play video">
                                      <IconButton
                                        color="secondary"
                                        onClick={() => playEmployeeVideo(row)}
                                        size="small"
                                      >
                                        <VideoLibrary />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : viewMode === "table" ? (
                    <TableContainer
                      component={Paper}
                      sx={{ mt: 1, width: "100%", position: "relative" }}
                    >
                      <Table
                        size="medium"
                        aria-label="pre-selection candidates table"
                        stickyHeader
                      >
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell
                              width="3%"
                              sx={{ textAlign: "center" }}
                            >
                              <Switch
                                checked={selectAll}
                                onChange={(e) => changeSelectAll(e)}
                                inputProps={{ "aria-label": "select all" }}
                                name="selectAll"
                                color="secondary"
                                size="small"
                              />
                            </StyledTableCell>
                            <StyledTableCell
                              width="2%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("sno")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="5%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("photo")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="15%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("name")}
                            </StyledTableCell>
                            <StyledTableCell
                              width="8%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("religion", cvLang)}
                            </StyledTableCell>
                            <StyledTableCell
                              width="8%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("maritalStatus", cvLang)}
                            </StyledTableCell>
                            <StyledTableCell
                              width="5%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("age", cvLang)}
                            </StyledTableCell>
                            <StyledTableCell
                              width="10%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("salary", cvLang)}
                            </StyledTableCell>
                            <StyledTableCell
                              width="8%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("arabic", cvLang)}
                            </StyledTableCell>
                            <StyledTableCell
                              width="8%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("english", cvLang)}
                            </StyledTableCell>
                            <StyledTableCell
                              width="17%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {tt("workExperience", cvLang)}
                            </StyledTableCell>
                            {/* <StyledTableCell width="10%" sx={{ textTransform: "uppercase" }}>
                              {t("partner")}
                            </StyledTableCell> */}
                            <StyledTableCell
                              width="10%"
                              sx={{ textTransform: "uppercase" }}
                            >
                              {t("actions")}
                            </StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {employees.map((row, index) => (
                            <StyledTableRow key={row._id}>
                              <StyledTableCell sx={{ textAlign: "center" }}>
                                <Switch
                                  checked={
                                    selectCandidates[index]
                                      ? selectCandidates[index]
                                      : false
                                  }
                                  onChange={(e) => changeCheckBox(e, index)}
                                  inputProps={{ "aria-label": "controlled" }}
                                  name={index.toString()}
                                  color="secondary"
                                  size="small"
                                />
                              </StyledTableCell>
                              <StyledTableCell>
                                <Typography color="primary" variant="h6">
                                  {(
                                    currentPage * rowsPerPage +
                                    index +
                                    1
                                  ).toLocaleString(
                                    theme.direction === "rtl"
                                      ? "ar-SA"
                                      : "en-US",
                                  )}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Avatar
                                  component={RouterLink}
                                  alt="avatar"
                                  src={row.facePhoto ? row.facePhoto : ""}
                                  sx={{
                                    cursor: "pointer",
                                    height: 40,
                                    width: 40,
                                  }}
                                  to={`/app/cvp/${row._id}`}
                                />
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{ color: "primary.main", fontWeight: 700 }}
                              >
                                {row.givenName}
                                <br />
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  {renderNationalityFlag(
                                    row.additionalData?.nationality as string,
                                  )}
                                  <span
                                    style={{
                                      color: theme.palette.text.secondary,
                                      fontWeight: 400,
                                      fontSize: "0.82rem",
                                    }}
                                  >
                                    {row.owner?.displayName.includes(" ")
                                      ? row.owner?.displayName.split(" ")[0]
                                      : row.owner?.displayName}
                                  </span>
                                </Stack>
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{
                                  color: "secondary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {tt(
                                  row.additionalData?.religion as string,
                                  cvLang,
                                )}
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{
                                  color: "primary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {tt(
                                  row.additionalData?.maritalStatus as string,
                                  cvLang,
                                )}
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{
                                  color: "primary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {Math.floor(
                                  differenceInMonths(
                                    new Date(),
                                    new Date(row.dateOfBirth as Date),
                                  ) / 12,
                                ).toLocaleString(
                                  theme.direction === "rtl" ? "ar-SA" : "en-US",
                                )}
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{
                                  color: "secondary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {row.additionalData?.salary != null
                                  ? `${row.additionalData.salary.toLocaleString(
                                      theme.direction === "rtl"
                                        ? "ar-SA"
                                        : "en-US",
                                    )}${row.additionalData.currency ? ` ${tt(row.additionalData.currency, cvLang)}` : ""}`
                                  : ""}
                              </StyledTableCell>
                              <StyledTableCell>
                                <Chip
                                  label={tt(
                                    row.education?.arabicLanguage as string,
                                    cvLang,
                                  )}
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                />
                              </StyledTableCell>
                              <StyledTableCell>
                                <Chip
                                  label={tt(
                                    row.education?.englishLanguage as string,
                                    cvLang,
                                  )}
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                                />
                              </StyledTableCell>
                              <StyledTableCell
                                sx={{
                                  color: "primary.main",
                                  fontWeight: 700,
                                }}
                              >
                                {!row.workExperience?.length
                                  ? tt("firstTimer", cvLang)
                                  : row.workExperience
                                      .map((exp) =>
                                        [exp.country, exp.years]
                                          .filter(Boolean)
                                          .map((v) => tt(v as string, cvLang))
                                          .join(" - "),
                                      )
                                      .filter(Boolean)
                                      .join(", ")}
                              </StyledTableCell>
                              {/* <StyledTableCell sx={{ color: "primary.main", fontWeight: 700 }}>
                                {row.owner?.displayName}
                              </StyledTableCell> */}
                              <StyledTableCell sx={{ whiteSpace: "nowrap" }}>
                                <Stack
                                  direction="row"
                                  spacing={0}
                                  alignItems="center"
                                  flexWrap="nowrap"
                                >
                                  <Tooltip title="View CV">
                                    <IconButton
                                      color="secondary"
                                      onClick={() =>
                                        viewCvHandler(row._id as string)
                                      }
                                      size="small"
                                    >
                                      <RemoveRedEye fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {row.employeeVideo && (
                                    <Tooltip title="Play video">
                                      <IconButton
                                        color="secondary"
                                        onClick={() => playEmployeeVideo(row)}
                                        size="small"
                                      >
                                        <VideoLibrary fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {row.status === EmployeeStatus.new &&
                                    isPrivilegedTransaction(
                                      user?.roles as Role[],
                                      "SelectCandidates",
                                    ) && (
                                      <>
                                        <Tooltip title={t("only_select")}>
                                          <IconButton
                                            color="secondary"
                                            onClick={() => justSelect(row)}
                                            size="small"
                                          >
                                            <ThumbUp fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("addAgreement")}>
                                          <IconButton
                                            color="secondary"
                                            onClick={() => selectCandidate(row)}
                                            size="small"
                                          >
                                            <Assignment fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  {row.status === EmployeeStatus.selected &&
                                    isPrivilegedTransaction(
                                      user?.roles as Role[],
                                      "DeselectCandidates",
                                    ) && (
                                      <>
                                        <Tooltip title={t("deselect")}>
                                          <IconButton
                                            color="secondary"
                                            onClick={() =>
                                              deSelectCandidate(
                                                row._id as string,
                                              )
                                            }
                                            size="small"
                                          >
                                            <ThumbDown fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("addAgreement")}>
                                          <IconButton
                                            color="secondary"
                                            onClick={() => selectCandidate(row)}
                                            size="small"
                                          >
                                            <Assignment fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  <Tooltip title="Share">
                                    <IconButton
                                      color="secondary"
                                      onClick={() =>
                                        shareSingle(row._id as string)
                                      }
                                      size="small"
                                    >
                                      <Share fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Grid container justifyContent="center">
                      {employees.map((row, index) => (
                        <Grid
                          item
                          lg={3}
                          md={4}
                          sm={6}
                          xs={12}
                          container
                          direction="column"
                          display="flex"
                          alignItems="center"
                          key={index}
                          sx={{
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: "5px",
                            boxShadow: "5px 5px rgba(0,0,0,0.14)",
                            mx: 1,
                            px: 1,
                          }}
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            badgeContent={
                              <Tooltip title={row.additionalData?.nationality}>
                                <Fab component="span" color="secondary">
                                  <Avatar
                                    alt="avatar"
                                    src={
                                      row.additionalData?.nationality ===
                                      Nationalities.ethiopian
                                        ? "/ethiopiaFlag.png"
                                        : row.additionalData?.nationality ===
                                            Nationalities.kenyan
                                          ? "/kenyaFlag.png"
                                          : row.additionalData?.nationality ===
                                              Nationalities.ugandan
                                            ? "/ugandaFlag.png"
                                            : "/philippineFlag.png"
                                    }
                                    sx={{
                                      height: 50,
                                      width: 50,
                                      // marginTop: -3,
                                    }}
                                  />
                                </Fab>
                              </Tooltip>
                            }
                          >
                            <Button
                              onClick={() => viewCvHandler(row._id as string)}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Image
                                  src={
                                    row && row.facePhoto
                                      ? (row.facePhoto as string)
                                      : "/blankShort.jpg"
                                  }
                                  width={200}
                                  height={200}
                                  shift="top"
                                  distance="2rem"
                                  shiftDuration={320}
                                  style={{ borderRadius: "50%" }}
                                />
                              </Stack>
                            </Button>
                          </Badge>

                          <TableContainer
                            component={Paper}
                            sx={{
                              marginTop: 1,
                              borderRadius: "10px",
                              width: "100%",
                            }}
                          >
                            <Table size="small" aria-label="simple table">
                              <TableHead>
                                <StyledTableRow key="3">
                                  <StyledTableCellLabel
                                    colSpan={4}
                                    sx={{ textAlign: "center" }}
                                  >
                                    {row.givenName +
                                      " (" +
                                      row.passportNumber +
                                      ")"}
                                    <br />
                                    {row.owner?.displayName}
                                  </StyledTableCellLabel>
                                </StyledTableRow>
                              </TableHead>
                              <TableBody>
                                <StyledTableRow key="1">
                                  <StyledTableCellLabel>
                                    {tt("age", cvLang)}
                                  </StyledTableCellLabel>
                                  <StyledTableCellValue>
                                    {Math.floor(
                                      differenceInMonths(
                                        new Date(),
                                        new Date(row.dateOfBirth as Date),
                                      ) / 12,
                                    ).toLocaleString(
                                      theme.direction === "rtl"
                                        ? "ar-SA"
                                        : "en-US",
                                    )}
                                  </StyledTableCellValue>

                                  <StyledTableCellLabel>
                                    {tt("salary", cvLang)}
                                  </StyledTableCellLabel>
                                  <StyledTableCellValue>
                                    {row.additionalData?.salary != null
                                      ? `${row.additionalData.salary.toLocaleString(
                                          theme.direction === "rtl"
                                            ? "ar-SA"
                                            : "en-US",
                                        )}${
                                          row.additionalData.currency
                                            ? ` ${tt(row.additionalData.currency, cvLang)}`
                                            : ""
                                        }`
                                      : ""}
                                  </StyledTableCellValue>
                                </StyledTableRow>
                                <StyledTableRow key="0">
                                  <StyledTableCellLabel>
                                    {tt("arabic", cvLang)}
                                  </StyledTableCellLabel>
                                  <StyledTableCellValue>
                                    {tt(
                                      row.education?.arabicLanguage as string,
                                      cvLang,
                                    )}
                                  </StyledTableCellValue>

                                  <StyledTableCellLabel>
                                    {tt("english", cvLang)}
                                  </StyledTableCellLabel>
                                  <StyledTableCellValue>
                                    {tt(
                                      row.education?.englishLanguage as string,
                                      cvLang,
                                    )}
                                  </StyledTableCellValue>
                                </StyledTableRow>
                                <StyledTableRow key="5">
                                  <StyledTableCellLabel>
                                    {tt("workExperience", cvLang)}
                                  </StyledTableCellLabel>
                                  <StyledTableCellValue colSpan={3}>
                                    {!row.workExperience?.length ? (
                                      <>{tt("firstTimer", cvLang)}</>
                                    ) : (
                                      <>
                                        {row.workExperience
                                          .map((experience) =>
                                            [
                                              experience.country,
                                              experience.years,
                                            ]
                                              .filter(Boolean)
                                              .map((value) =>
                                                tt(value as string, cvLang),
                                              )
                                              .join("-"),
                                          )
                                          .filter(Boolean)
                                          .map((line, index) => (
                                            <span key={index}>
                                              {index > 0 && <br />}
                                              {line}
                                            </span>
                                          ))}
                                      </>
                                    )}
                                  </StyledTableCellValue>
                                </StyledTableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            sx={{ width: "100%" }}
                          >
                            <Stack direction="row" spacing={1} sx={{ m: 1 }}>
                              {row.status === EmployeeStatus.new &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "SelectCandidates",
                                ) && (
                                  <>
                                    <Tooltip title={t("only_select")}>
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => justSelect(row)}
                                        size="small"
                                      >
                                        <ThumbUp />
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title={t("addAgreement")}>
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => selectCandidate(row)}
                                        size="small"
                                      >
                                        <Assignment />
                                      </Button>
                                    </Tooltip>
                                  </>
                                )}
                              {row.status === EmployeeStatus.selected &&
                                isPrivilegedTransaction(
                                  user?.roles as Role[],
                                  "DeselectCandidates",
                                ) && (
                                  <>
                                    <Tooltip title={t("deselect")}>
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={() =>
                                          deSelectCandidate(row._id as string)
                                        }
                                        size="small"
                                      >
                                        <ThumbDown />
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title={t("addAgreement")}>
                                      <Button
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => selectCandidate(row)}
                                        size="small"
                                      >
                                        <Assignment />
                                      </Button>
                                    </Tooltip>
                                  </>
                                )}
                              <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => shareSingle(row._id as string)}
                                size="small"
                              >
                                <Share />
                              </Button>
                            </Stack>
                            <Switch
                              checked={
                                selectCandidates[index]
                                  ? selectCandidates[index]
                                  : false
                              }
                              onChange={(e) => changeCheckBox(e, index)}
                              inputProps={{ "aria-label": "controlled" }}
                              name={index.toString()}
                              color="secondary"
                            />
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Stack>
              </Box>
              <FilterSidebar listType="candidates" />
            </Stack>
            {employees.length > 0 && (
              <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
                {!isPostSelectionTab(activeTab) && (
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    justifyItems="center"
                    sx={{
                      "& > :not(style)": {
                        m: 0,
                        ml: 1,
                      },
                    }}
                  >
                    <FormControlLabel
                      style={{ marginBottom: "0" }}
                      control={
                        <Switch
                          checked={selectAll}
                          onChange={(e) => changeSelectAll(e)}
                          inputProps={{ "aria-label": "controlled" }}
                          name="selectAll"
                          color="secondary"
                        />
                      }
                      label="Select All"
                    />
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={shareList}
                      size="large"
                      sx={{ mt: 1 }}
                      disabled={!selectCandidates.some((c) => c === true)}
                    >
                      <Share />
                    </Button>
                  </Stack>
                )}
                <Typography
                  alignSelf="center"
                  justifyContent="center"
                  variant="h6"
                  color="white"
                  sx={{
                    backgroundColor: "primary.main",
                    p: 1,
                    borderRadius: "4px",
                  }}
                >
                  {t("total")}: {total}
                </Typography>

                <Paging
                  total={total}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  setRowsPerPage={setRowsPerPage}
                  setCurrentPage={setCurrentPage}
                />
              </Stack>
            )}
          </>

          {/*{employees.length > 0 && (
            <Accordion>
              <StyledAccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h6" component="div">
                  <Share />
                  Shared Links
                </Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                <Stack>
                  {sharedLinks && sharedLinks.length > 1 && (
                    <TableContainer component={Paper}>
                      <Table size="small" aria-label="simple table">
                        <TableHead>
                          <StyledTableRow>
                            <StyledTableCell>{t("sno")}</StyledTableCell>
                            <StyledTableCell>{t("link")}</StyledTableCell>
                            <StyledTableCell>{t("employees")}</StyledTableCell>

                            <StyledTableCell>{t("actions")}</StyledTableCell>
                          </StyledTableRow>
                        </TableHead>
                        <TableBody>
                          {sharedLinks.map((row, index) => (
                            <StyledTableRow key={index}>
                              <StyledTableCell>{index + 1}</StyledTableCell>
                              <StyledTableCell>{row.link}</StyledTableCell>
                              <StyledTableCell>
                                {row.employees
                                  ?.map((e) => e.givenName)
                                  .join(", ")}
                              </StyledTableCell>

                              <StyledTableCell>
                                <IconButton
                                  color="secondary"
                                  onClick={() =>
                                    openShareDialogue(row._id as string)
                                  }
                                  size="large"
                                >
                                  <Share />
                                </IconButton>
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )} */}

          {success && <Toast severity="success">{success.message}</Toast>}
          {error && <Toast severity="error">{error.message}</Toast>}
          {open && (
            <CustomDialog
              title="Share Link"
              isOpen={open}
              handleDialogClose={dialogClose}
            >
              <ShareCvs
                employeeIds={sharedEmployeeIds}
                handleDialogClose={dialogCloseVisa}
              />
            </CustomDialog>
          )}
          {openVisa && (
            <CustomDialog
              title="Select Candidate"
              isOpen={openVisa}
              handleDialogClose={dialogCloseVisa}
            >
              <VisaEntry
                currentEmployee={selectedEmployee}
                handleDialogClose={dialogCloseVisa}
              />
            </CustomDialog>
          )}
          <Dialog
            open={Boolean(videoPreview)}
            onClose={() => setVideoPreview(null)}
            maxWidth="md"
          >
            <Box sx={{ p: 1.5, bgcolor: "background.default" }}>
              {videoPreview?.name && (
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mb: 1, px: 0.5 }}
                >
                  {videoPreview.name}
                </Typography>
              )}
              {videoPreview?.url && (
                <Box
                  component="video"
                  src={videoPreview.url}
                  controls
                  autoPlay
                  playsInline
                  sx={{
                    width: { xs: "85vw", md: 720 },
                    maxHeight: { xs: "70vh", md: 620 },
                    borderRadius: 2,
                    bgcolor: "common.black",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              )}
            </Box>
          </Dialog>
        </>
      </Box>
    </>
  );
};
